import type { ReactNode } from 'react';

import type { BlocoDocumento } from '@/shared/components/documentos/paginado';
import type { ChatMessageButtons } from '@/shared/types/chatMessageButtonTypes';
import type { MensagemStatus } from '@/shared/types/mensagemStatus';

import type {
  NegociacaoCasoDetail,
  NegociacaoTelefoneOrigemUi,
  NegociacaoTelefoneStatusUi,
} from './negociacaoDetailTypes';
import type { AtorMarco } from './negociacaoMarcoTypes';

/**
 * Espelho do contrato do core (`certificado/types/certificadoNegociacao.types.ts`, CU-17tpepcqtqe).
 * Datas são ISO 8601 em UTC; a tela converte para America/Sao_Paulo. `emissao.id` só serve para
 * pedir o PDF da MESMA emissão exibida e nunca é mostrado.
 */
export type StatusDoPdfDaEmissao = 'PENDENTE' | 'GERANDO' | 'GERADO' | 'FALHOU';

export const FONTES_DO_CERTIFICADO = ['CASO', 'CONTATOS', 'MENSAGENS', 'MARCOS', 'CAMPOS'] as const;

export type FonteDoCertificado = (typeof FONTES_DO_CERTIFICADO)[number];

export interface EmissaoDoCertificadoNegociacao {
  id: string;
  versao: number;
  codigoAutenticacao: string;
  urlValidacao: string;
  dataEmissao: string;
  recorteAte: string | null;
  hashConteudo: string;
  pdf: StatusDoPdfDaEmissao;
}

export interface CasoDoCertificadoNegociacao {
  numeroNegociacao: string | null;
  assunto: string;
  status: string;
  encerradoEm: string | null;
  reabertoEm: string | null;
}

export interface PessoaDoCertificadoNegociacao {
  nome: string;
  tipoPessoa: 'FISICA' | 'JURIDICA' | null;
  /** CPF/CNPJ já formatado pelo core. */
  documento: string | null;
}

export type TipoDeCampoPersonalizado =
  | 'STRING'
  | 'NUMBER'
  | 'FINANCEIRO'
  | 'BOOLEAN'
  | 'DATE'
  | 'JSON'
  | 'ENUM';

export interface CampoPersonalizadoDoCertificado {
  rotulo: string;
  tipo: TipoDeCampoPersonalizado;
  valorFormatado: string;
}

export interface PorteiroDoContato {
  verificadoEm: string | null;
  /** APTO é "tem WhatsApp ativo", nunca identidade confirmada. */
  resultado: 'APTO' | 'BARRADO' | null;
  detalhe: string | null;
}

/**
 * No contato derivado (legado, sem linha de fila) situação, tentativas, origem e porteiro são nulos.
 *
 * Os campos `*Rotulo` (e `textoExibido`/`midia.rotulo` da mensagem) vêm calculados pelo core e
 * congelados no conteúdo da emissão, dentro do hash: a tela e o PDF só os imprimem. Recalcular
 * aqui deixaria a tela de uma emissão divergir do PDF dela quando uma regra mudasse de um lado só.
 */
export interface ContatoDoCertificado {
  /** 'contato-N': só liga o contato ao bloco de conversa; não é exibido. */
  chave: string;
  numero: string;
  ordem: number | null;
  origem: NegociacaoTelefoneOrigemUi | null;
  situacao: NegociacaoTelefoneStatusUi | null;
  situacaoRotulo: string;
  porteiroRotulo: string;
  tentativas: number | null;
  iniciadoEm: string | null;
  esgotadoEm: string | null;
  porteiro: PorteiroDoContato | null;
  indicadoPor: { numero: string } | null;
  descarte: { em: string; motivo: 'NEGOU_IDENTIDADE' | 'NUMERO_INCORRETO_INFORMADO' | null } | null;
  falhaTecnica: { em: string; motivo: string | null } | null;
}

export interface MarcoDoCertificado {
  tipo: string;
  titulo: string;
  descricao: string | null;
  ator: AtorMarco;
  dataHora: string;
}

export type TipoDeAutoriaDaMensagem = 'ATENDENTE' | 'LEXI' | 'CONTRAPARTE' | 'NAO_IDENTIFICADO';

export type TipoDeMensagemDoCertificado = 'TEXT' | 'AUDIO' | 'IMAGE' | 'DOCUMENT' | 'VIDEO';

export interface MidiaDaMensagem {
  tipo: TipoDeMensagemDoCertificado;
  rotulo: string;
  referencia: string | null;
  semTexto: boolean;
}

export interface MensagemDoCertificado {
  direcao: 'INBOUND' | 'OUTBOUND';
  autoria: { tipo: TipoDeAutoriaDaMensagem; nome: string | null };
  autoriaRotulo: string;
  dataHora: string;
  tipo: TipoDeMensagemDoCertificado;
  texto: string;
  textoExibido: string;
  status: MensagemStatus;
  entregueEm: string | null;
  lidaEm: string | null;
  falha: { motivo: string | null } | null;
  midia: MidiaDaMensagem | null;
  /** Rótulos congelados na emissão; ausente nas emissões anteriores ao suporte a opções. */
  opcoes?: ChatMessageButtons;
}

/**
 * O bloco de conversa a que a mensagem pertence. `indice` (1-based) é a ordem do bloco e o "N" de
 * "Conversa N"; `totalDeMensagens` é o do bloco inteiro, e não o da página. `contatoChave` e
 * `numero` nulos formam o bloco 'Número não informado' (conversa só-BSUID).
 */
export interface BlocoDaMensagemDoCertificado {
  indice: number;
  contatoChave: string | null;
  numero: string | null;
  totalDeMensagens: number;
}

export interface ItemDeMensagemDoCertificado {
  bloco: BlocoDaMensagemDoCertificado;
  mensagem: MensagemDoCertificado;
}

/** Um bloco de conversa do documento, montado com as mensagens já recebidas dele. */
export interface ConversaDoCertificado extends BlocoDaMensagemDoCertificado {
  mensagens: MensagemDoCertificado[];
}

export interface ContagensDoCertificado {
  numerosRegistrados: number;
  mensagens: number;
  eventos: number;
}

/**
 * `POST .../certificado/emissao-atual`: a emissão, a identificação e as contagens. As coleções
 * (campos, contatos, marcos e mensagens) vêm paginadas pela rota de seções da mesma emissão.
 */
export interface CertificadoNegociacaoResumoDto {
  emissao: EmissaoDoCertificadoNegociacao;
  caso: CasoDoCertificadoNegociacao;
  pessoa: PessoaDoCertificadoNegociacao;
  contagens: ContagensDoCertificado;
}

/** Na ordem do documento, que é a ordem em que a página pede as seções. */
export const SECOES_DO_CERTIFICADO = ['campos', 'contatos', 'marcos', 'mensagens'] as const;

export type SecaoDoCertificado = (typeof SECOES_DO_CERTIFICADO)[number];

export interface ItensDasSecoesDoCertificado {
  campos: CampoPersonalizadoDoCertificado;
  contatos: ContatoDoCertificado;
  marcos: MarcoDoCertificado;
  mensagens: ItemDeMensagemDoCertificado;
}

/** `GET .../emissoes/:emissaoId/secoes/:secao`. O `nextCursor` é opaco: só volta ao core. */
export interface PaginaDaSecaoDoCertificado<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export type PaginaRecebidaDoCertificado = {
  [S in SecaoDoCertificado]: {
    secao: S;
    pagina: PaginaDaSecaoDoCertificado<ItensDasSecoesDoCertificado[S]>;
  };
}[SecaoDoCertificado];

export interface PedidoDePaginaDoCertificado {
  secao: SecaoDoCertificado;
  cursor: string | null;
}

export interface CarregarPaginaDoCertificadoInput extends PedidoDePaginaDoCertificado {
  casoId: string;
  emissaoId: string;
}

/** `iniciada` separa "ainda não pedida" de "pedida e vazia": só a segunda mostra o estado vazio. */
export interface SecaoCarregadaDoCertificado<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
  iniciada: boolean;
}

export type SecoesCarregadasDoCertificado = {
  [S in SecaoDoCertificado]: SecaoCarregadaDoCertificado<ItensDasSecoesDoCertificado[S]>;
};

/**
 * As páginas já recebidas das seções de UMA emissão. Trocar de emissão descarta tudo, e a resposta
 * de outra emissão, ou de um pedido que não é o corrente (`requestId`), é ignorada.
 */
export interface SecoesDoCertificadoState extends SecoesCarregadasDoCertificado {
  emissaoId: string | null;
  requestId: string | null;
  carregando: boolean;
  /** A seção cuja página falhou: o Tentar novamente pede a mesma página de novo. */
  falhou: SecaoDoCertificado | null;
}

/** Uma seção como o documento a lê: `completa` só quando o core disse que não há mais páginas. */
export interface SecaoDoDocumentoDoCertificado<T> {
  items: T[];
  completa: boolean;
}

export interface SecoesDoDocumentoDoCertificado {
  campos: SecaoDoDocumentoDoCertificado<CampoPersonalizadoDoCertificado>;
  contatos: SecaoDoDocumentoDoCertificado<ContatoDoCertificado>;
  marcos: SecaoDoDocumentoDoCertificado<MarcoDoCertificado>;
  conversas: SecaoDoDocumentoDoCertificado<ConversaDoCertificado>;
}

export type CarregamentoDasSecoesDoCertificado = 'carregando' | 'falhou' | 'completo';

/**
 * Falha da emissão como o thunk a entrega. `status` separa o 404 (página "não encontrado") dos
 * demais; `code` e `fonte` vêm do envelope de erro do core (`error.code`, `error.details.fonte`).
 * Sem resposta (rede), os três são nulos.
 */
export interface ErroDoCertificadoNegociacao {
  status: number | null;
  code: string | null;
  fonte: FonteDoCertificado | null;
}

export interface CertificadoNegociacaoState {
  casoId: string | null;
  requestId: string | null;
  dados: CertificadoNegociacaoResumoDto | null;
  loading: boolean;
  error: ErroDoCertificadoNegociacao | null;
  baixandoPdf: boolean;
  /**
   * Quantos acontecimentos do caso (mensagem, status de mensagem, conversa, status do caso,
   * direcionamento) chegaram pelo tempo real. A consulta aberta compara o número, e não o evento,
   * para pedir a emissão de novo.
   */
  acontecimentos: number;
  /**
   * As conversas da fila de telefones do caso, que o certificado pede por conta própria: fora do
   * detalhe (página avulsa) a fila do detalhe não é guardada, e sem ela a mensagem de um número
   * antigo, que pode chegar sem o caso no `metadata`, não seria reconhecida.
   */
  conversasDaFila: ConversasConhecidasDoCaso | null;
  /**
   * O `updatedAt` de cada conversa do caso na última vez que ela chegou ao store. O catch-up de cada
   * reconexão do stream reentrega as mesmas conversas (o `lastEventAt` só avança com evento do
   * stream), e sem esta memória cada reconexão contava como acontecimento e pedia a emissão de novo.
   */
  conversasAtualizadasEm: Record<string, string>;
  secoes: SecoesDoCertificadoState;
}

export interface ConversasConhecidasDoCaso {
  casoId: string;
  conversaIds: string[];
}

/** A emissão só existe inteira: carregando e erro não têm contagem nem conteúdo (CA-20). */
export type EstadoDoCertificadoNegociacao =
  | { fase: 'carregando' }
  | { fase: 'erro'; erro: ErroDoCertificadoNegociacao }
  | { fase: 'pronto'; certificado: CertificadoNegociacaoResumoDto };

export interface OpcoesDoCertificadoNegociacao {
  /** O "Baixar PDF" da aba abre a página pedindo o download da emissão que ela carregar. */
  baixarAoAbrir?: boolean;
}

export interface OpcoesDaRotaDoCertificado {
  baixar?: boolean;
}

export interface SolicitarPdfDoCertificadoInput {
  casoId: string;
  emissaoId: string;
  nomeArquivo: string;
}

export interface UrlDeValidacaoExibivel {
  href: string;
  exibicao: string;
}

export interface CertificadoNegociacaoTabProps {
  caso: NegociacaoCasoDetail;
  statusLabel: string;
}

export interface CertificadoNegociacaoDocumentoProps {
  certificado: CertificadoNegociacaoResumoDto;
  secoes: SecoesDoDocumentoDoCertificado;
}

export interface CertificadoNegociacaoPageProps {
  caseId: string;
  baixarAoAbrir?: boolean;
}

/**
 * Até onde uma fatia de texto do documento cresce antes de virar outra: em caracteres (UTF-16), em
 * linhas e em colunas estimadas por linha. Cada lugar do documento tem a sua largura e altura.
 */
export interface FatiaDoTextoCertificado {
  maxCaracteres: number;
  maxLinhas: number;
  maxColunas: number;
}

export interface LinhaTabelaCertificado {
  id: string;
  celulas: ReactNode[];
}

export interface TabelaCertificadoInput {
  id: string;
  cabecalhos: string[];
  colunas: string;
  linhas: LinhaTabelaCertificado[];
}

export interface FatiaTabelaCertificadoProps extends TabelaCertificadoInput {
  indice: number;
  primeiroDaPagina: boolean;
}

export interface FatiaDaMensagemDoCertificado {
  parte: string;
  continuacao: boolean;
  opcoes?: ChatMessageButtons;
}

export interface FatiaConversaCertificadoProps extends FatiaDaMensagemDoCertificado {
  conversa: ConversaDoCertificado;
  mensagem: MensagemDoCertificado;
  primeira: boolean;
  ultima: boolean;
  primeiroDaPagina: boolean;
  mostrarData: boolean;
}

/**
 * Um pedaço do documento, na ordem dele. `completo: false` é o pedaço que ainda espera páginas: o
 * documento mostra o que já chegou dele e para ali, sem montar nada do que vem depois.
 */
export interface TrechoDoDocumentoDoCertificado {
  blocos: BlocoDocumento[];
  completo: boolean;
}

export interface AvisoDoCertificadoProps {
  texto: string;
  /** Sem ação, o aviso não oferece Tentar novamente. */
  aoTentarNovamente?: () => void;
}
